/**
 * 新潟県の背景ツアー「新潟めぐり」コンテンツ。
 *
 * 軸は一日の時間。朝の星峠の棚田から、日没の萬代橋へ。
 * 佐渡に2か所、魚沼・十日町に2か所、残りを上越・中越・下越に配している。
 *
 * 佐渡島の金山について。
 *  世界遺産の登録名称は「佐渡島（さど）の金山」で、2024年7月27日に登録された。
 *  構成資産は西三川砂金山、鶴子銀山、相川金銀山。
 *  鉱山労働の歴史をもつ産業遺産なので、にぎやかな扱いはしない。
 *  公式で確認できたのは「鑚と穿鎚だけが頼りの手掘り作業」「排水作業の便を考慮して
 *  坑道が掘られた」まで。それ以上のことは書かない。
 *  近代の選鉱場跡などの施設は絵に入れず、山が割れた姿だけを描く。
 *
 * 現代アートは扱わない。
 *  清津峡渓谷トンネルの終点にある水盤の演出は現代美術作品なので、
 *  トンネル内は描かせない。峡谷そのもの（柱状節理の断崖と川）だけにする。
 *  大地の芸術祭の作品も同じ理由ですべて外した。
 *
 * トキについて。
 *  現在の野生個体数は環境省・新潟県・佐渡市の公式ページに記載がなかった。
 *  書けるのは「平成26年に60羽定着を達成」までなので、今回は景色に入れていない。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 星峠の棚田の「雲海」は十日町市公式に記述が無い。「水鏡」だけを書く。
 *  - 苗名滝は「55メートル下にある岩に滝の水が落ちてぶつかるときの轟音」が公式の表現。
 *    「落差55メートル」と縮めない。
 *  - 笹川流れの海水の透明度、大野亀の高さ、トビシマカンゾウの株数は公式に記載が無い。
 *  - 荻ノ島の茅葺き民家の軒数も公式に記載が無い。
 *  - 星峠は私有の農地。田や畔に入らない、農作業中の人を無断で撮影しない、と
 *    十日町市公式が注意している。絵にも人を描かない。
 *  - 出雲崎・荻ノ島は今も人が住む集落。特定の家や表札を描かない。
 */

import type { StageTour } from '../types';

export const niigataSceneryTour: StageTour = {
  scenes: [
    {
      id: 'hoshitoge-tanada',
      name: '星峠の棚田',
      area: '十日町市・朝',
      image: '/assets/scenery/niigata/scenes/01_hoshitoge_tanada.webp',
      hotspots: [
        {
          id: 'hoshitoge-200',
          x: 0.5,
          y: 0.52,
          title: '大小約200枚',
          body: '大小さまざまな田んぼ約200枚が、まるで魚の鱗のように斜面を埋めつくすように広がっています。',
          source:
            'https://www.city.tokamachi.lg.jp/soshiki/matsudaishisho/chiikishinkoka/1/gyomu/1455525138230.html',
          mapQuery: '星峠の棚田',
        },
        {
          id: 'hoshitoge-mizukagami',
          x: 0.24,
          y: 0.66,
          title: '水鏡',
          body: '田んぼに水が入って、青空や周囲の風景を映す「水鏡」の美しい風景が見られます。',
          source:
            'https://www.city.tokamachi.lg.jp/soshiki/matsudaishisho/chiikishinkoka/1/gyomu/1455525138230.html',
          mapQuery: '十日町市 松代',
        },
        {
          id: 'hoshitoge-fuyu',
          x: 0.76,
          y: 0.34,
          title: '冬は通れない',
          body: '冬季は雪があり通行できません。例年11月下旬から4月中旬までです。田や畑、畔には入らず、農作業中の方を無断で撮影しないよう案内されています。',
          source:
            'https://www.city.tokamachi.lg.jp/soshiki/matsudaishisho/chiikishinkoka/1/gyomu/1455525138230.html',
          mapQuery: '星峠',
        },
      ],
    },
    {
      id: 'hyoko',
      name: '瓢湖の白鳥',
      area: '阿賀野市・冬の朝',
      image: '/assets/scenery/niigata/scenes/02_hyoko.webp',
      hotspots: [
        {
          id: 'hyoko-ramsar',
          x: 0.5,
          y: 0.5,
          title: 'ラムサール条約の登録湿地',
          body: '瓢湖は平成20年10月30日に、国内36番目のラムサール条約登録湿地になりました。このとき特別保護地区の面積も8ヘクタールから24ヘクタールに広がりました。',
          source: 'https://www.city.agano.niigata.jp/soshiki/koenkanrijimusho/2/2805.html',
          mapQuery: '瓢湖',
        },
        {
          id: 'hyoko-tennen',
          x: 0.24,
          y: 0.64,
          title: '水原のハクチョウ渡来地',
          body: '昭和29年3月に「水原のハクチョウ渡来地」として国の天然記念物に指定されました。',
          source: 'https://www.city.agano.niigata.jp/soshiki/koenkanrijimusho/2/2805.html',
          mapQuery: '瓢湖 白鳥',
        },
        {
          id: 'hyoko-6800',
          x: 0.76,
          y: 0.34,
          title: '最高6,800羽',
          body: '昭和25年に戦後初めて27羽が渡来し、昭和53年に最高渡来数が1,000羽、平成12年11月には6,800羽となりました。寛永3年（1626年）の創設で、瓢箪に似ていることから瓢湖と呼ばれます。',
          source: 'https://www.city.agano.niigata.jp/soshiki/koenkanrijimusho/2/2805.html',
          mapQuery: '阿賀野市 瓢湖',
        },
      ],
    },
    {
      id: 'kiyotsukyo',
      name: '清津峡',
      area: '十日町市・午前',
      image: '/assets/scenery/niigata/scenes/03_kiyotsukyo.webp',
      hotspots: [
        {
          id: 'kiyotsu-3daikyokoku',
          x: 0.5,
          y: 0.5,
          title: '日本三大峡谷に数えられる',
          body: '清津峡は黒部峡谷、大杉谷とともに日本三大峡谷の一つに数えられ、国の名勝・天然記念物にも指定されています。',
          source:
            'https://www.city.tokamachi.lg.jp/soshiki/nakasatoshisho/chiikishinkoka/1/gyomu/1450421453063.html',
          mapQuery: '清津峡',
        },
        {
          id: 'kiyotsu-1941',
          x: 0.24,
          y: 0.36,
          title: '昭和16年の指定',
          body: '国指定文化財等データベースによると、指定年月日は1941年（昭和16年）4月23日、種別は名勝および天然記念物です。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/856',
          mapQuery: '清津峡 名勝',
        },
        {
          id: 'kiyotsu-20km',
          x: 0.76,
          y: 0.64,
          title: '約20キロの大峡谷',
          body: '指定の解説には、清津川の上流にある延長約20キロの大峡谷で、美麗な柱状節理をなす岩と堅緻な緑色凝灰岩が峡谷成立の主因をなす、とあります。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/856',
          mapQuery: '清津峡渓谷',
        },
      ],
    },
    {
      id: 'doyu-no-warito',
      name: '道遊の割戸',
      area: '佐渡市・昼前',
      image: '/assets/scenery/niigata/scenes/04_doyu_no_warito.webp',
      hotspots: [
        {
          id: 'doyu-30m',
          x: 0.5,
          y: 0.44,
          title: '幅30メートル、深さ74メートル',
          body: '山頂部の割れ目は幅30メートル、深さ74メートルに及びます。現在も佐渡金銀山遺跡を象徴する存在として親しまれています。',
          source: 'https://www.city.sado.niigata.jp/site/bunkazai/5180.html',
          mapQuery: '道遊の割戸',
        },
        {
          id: 'doyu-tebori',
          x: 0.24,
          y: 0.6,
          title: '鑚と穿鎚だけの手掘り',
          body: '排水のための坑道「南沢疎水道」は全線が岩盤で、鑚（たがね）と穿鎚（つち）だけが頼りの手掘り作業でした。道遊の割戸は、山の頂が真っ二つに掘り割られている姿から、古くは「青柳割戸」と呼ばれていました。',
          source: 'https://www.city.sado.niigata.jp/site/bunkazai/5180.html',
          mapQuery: '相川金銀山',
        },
        {
          id: 'doyu-isan',
          x: 0.76,
          y: 0.32,
          title: '世界文化遺産「佐渡島の金山」',
          body: '2024年（令和6年）7月27日に世界文化遺産に登録されました。構成資産は西三川砂金山、鶴子銀山、相川金銀山です。',
          source: 'https://www.pref.niigata.lg.jp/site/sado-goldmine/',
          mapQuery: '佐渡島の金山',
        },
      ],
    },
    {
      id: 'onogame',
      name: '大野亀とトビシマカンゾウ',
      area: '佐渡市・6月の昼',
      image: '/assets/scenery/niigata/scenes/05_onogame.webp',
      hotspots: [
        {
          id: 'onogame-kanzou',
          x: 0.5,
          y: 0.6,
          title: '一面を黄色に埋めるカンゾウ',
          body: '大佐渡の北に位置する大野亀を、一面黄色に埋め尽くすように咲き誇るトビシマカンゾウが有名です。',
          source: 'https://www.pref.niigata.lg.jp/site/sado-kikaku/1337896842975.html',
          mapQuery: '大野亀',
        },
        {
          id: 'onogame-tobishima',
          x: 0.24,
          y: 0.42,
          title: '佐渡と飛島にしか生えない',
          body: 'トビシマカンゾウは佐渡のほか、山形県酒田市の飛島と酒田海岸にしか生息しないススキノキ科の多年草で、平成17年に佐渡市の花に選定されました。見頃は6月第2週頃です。',
          source: 'https://www.pref.niigata.lg.jp/site/sado-kikaku/1337896842975.html',
          mapQuery: 'トビシマカンゾウ',
        },
        {
          id: 'onogame-kaifu',
          x: 0.76,
          y: 0.32,
          title: '名勝「佐渡海府海岸」',
          body: '国名勝「佐渡海府海岸」は昭和9年5月1日の指定で、下相川から大佐渡北端の弾崎まで延長約50キロ。大野亀島や二ツ亀島に代表される北区は硬い粒状玄武岩からなり、陸地が大斜面の状態で海になだれ込みます。',
          source: 'https://www.city.sado.niigata.jp/site/bunkazai/5214.html',
          mapQuery: '佐渡海府海岸',
        },
      ],
    },
    {
      id: 'naenataki',
      name: '苗名滝',
      area: '妙高市・午後',
      image: '/assets/scenery/niigata/scenes/06_naenataki.webp',
      hotspots: [
        {
          id: 'naena-nai',
          x: 0.5,
          y: 0.5,
          title: '地震を意味する「なゐ」',
          body: '名の由来は、昔の日本語で地震を意味する「なゐ」。55メートル下にある岩に滝の水が落ちてぶつかるときの轟音にちなんだものです。発音が変化して「なえ」となり、名前を表す「な」が加わって「なえな滝」になりました。',
          source: 'https://www.city.myoko.niigata.jp/docs/31651.html',
          mapQuery: '苗名滝',
        },
        {
          id: 'naena-1814',
          x: 0.24,
          y: 0.64,
          title: '1814年に今の形に',
          body: '苗名滝は1814年に現在の形になりました。俳人の小林一茶が1813年4月に訪れ、句を詠んでいます。',
          source: 'https://www.city.myoko.niigata.jp/docs/31651.html',
          mapQuery: '妙高市 苗名滝',
        },
        {
          id: 'naena-chujo',
          x: 0.76,
          y: 0.34,
          title: '柱状節理の岩壁',
          body: '新潟県妙高市と長野県信濃町の境に位置する、日本の滝100選に選ばれている滝です。柱状節理の岩壁から水しぶきを上げて落ち込むさまは迫力満点です。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/myoko-togakushi-renzan/spot/',
          mapQuery: '妙高戸隠連山国立公園',
        },
      ],
    },
    {
      id: 'sasagawa-nagare',
      name: '笹川流れ',
      area: '村上市・午後',
      image: '/assets/scenery/niigata/scenes/07_sasagawa_nagare.webp',
      hotspots: [
        {
          id: 'sasagawa-11km',
          x: 0.5,
          y: 0.52,
          title: '約11キロの海岸線',
          body: '村上市浜新保集落内の鳥越山から、村上市寒川集落内の狐崎までの約11キロメートルの海岸線を指します。',
          source: 'https://www.city.murakami.lg.jp/site/kanko/sasagawanagare.html',
          mapQuery: '笹川流れ',
        },
        {
          id: 'sasagawa-meisho',
          x: 0.24,
          y: 0.38,
          title: '国の名勝天然記念物',
          body: '国の名勝天然記念物に指定されており、県立自然公園でもあります。',
          source: 'https://www.city.murakami.lg.jp/site/kanko/sasagawanagare.html',
          mapQuery: '笹川流れ 名勝',
        },
        {
          id: 'sasagawa-kigan',
          x: 0.76,
          y: 0.64,
          title: '名前のついた奇岩',
          body: '眼鏡岩、びょうぶ岩、恐竜岩、舞子岩、ニタリ岩、君戻し岩、汐吹岩といった奇岩があります。',
          source: 'https://www.city.murakami.lg.jp/site/kanko/sasagawanagare.html',
          mapQuery: '笹川流れ 眼鏡岩',
        },
      ],
    },
    {
      id: 'oginoshima',
      name: '荻ノ島かやぶき環状集落',
      area: '柏崎市・夕方前',
      image: '/assets/scenery/niigata/scenes/08_oginoshima.webp',
      hotspots: [
        {
          id: 'ogino-kanjo',
          x: 0.5,
          y: 0.54,
          title: '全国でも珍しい環状集落',
          body: 'かやぶき屋根の家が田んぼを囲んで環状に点在する、全国でも珍しいかやぶきの環状集落です。',
          source:
            'https://www.city.kashiwazaki.lg.jp/soshikiichiran/shiminseikatsubu/chiikijimusho/takayanagichojimusho/1/5756.html',
          mapQuery: '荻ノ島集落',
        },
        {
          id: 'ogino-3_5km',
          x: 0.24,
          y: 0.4,
          title: '高柳の山あい',
          body: '高柳町の中心部から3.5キロメートルほど山あいに入ったところにある集落です。',
          source:
            'https://www.city.kashiwazaki.lg.jp/soshikiichiran/shiminseikatsubu/chiikijimusho/takayanagichojimusho/1/5756.html',
          mapQuery: '柏崎市 高柳町',
        },
        {
          id: 'ogino-ryori',
          x: 0.76,
          y: 0.32,
          title: 'かやぶきの家に泊まれる',
          body: '集落のかやぶき民家「島の家」と「荻の家」は貸別荘になっていて、宿泊や日帰りで利用できます。',
          source:
            'https://www.city.kashiwazaki.lg.jp/soshikiichiran/shiminseikatsubu/chiikijimusho/takayanagichojimusho/1/5756.html',
          mapQuery: '荻ノ島 かやぶきの宿',
        },
      ],
    },
    {
      id: 'izumozaki-tsumairi',
      name: '出雲崎の妻入りの街並み',
      area: '出雲崎町・夕',
      image: '/assets/scenery/niigata/scenes/09_izumozaki_tsumairi.webp',
      hotspots: [
        {
          id: 'izumozaki-4km',
          x: 0.5,
          y: 0.5,
          title: '約4キロ続く妻入りの街並み',
          body: '通りに面して大棟が直角になっている「妻入り」という形式の街並みが、海岸線に沿い約4キロも続いています。出雲崎町は、これを日本一の長さと紹介しています。',
          source: 'https://www.town.izumozaki.niigata.jp/kanko/spot/tsumari.html',
          mapQuery: '出雲崎 妻入りの街並み',
        },
        {
          id: 'izumozaki-maguchi',
          x: 0.24,
          y: 0.36,
          title: '間口が狭く奥行きが長い',
          body: '江戸時代の出雲崎は人口密度が高く、多くの人が住めるように、間口が狭く奥行きの長い妻入りの形式がとられていました。',
          source: 'https://www.town.izumozaki.niigata.jp/kanko/spot/tsumari.html',
          mapQuery: '出雲崎町',
        },
        {
          id: 'izumozaki-nengu',
          x: 0.76,
          y: 0.64,
          title: '間口で税金が決まった',
          body: '間口によって税金が掛けられていたとのことで、二間や三間半の家が多く並んでいます。',
          source: 'https://www.town.izumozaki.niigata.jp/kanko/spot/tsumari.html',
          mapQuery: '出雲崎 町並み',
        },
      ],
    },
    {
      id: 'bandaibashi',
      name: '萬代橋',
      area: '新潟市・日没',
      image: '/assets/scenery/niigata/scenes/10_bandaibashi.webp',
      hotspots: [
        {
          id: 'bandai-6ren',
          x: 0.5,
          y: 0.52,
          title: '鉄筋コンクリート造6連アーチ橋',
          body: '萬代橋は鉄筋コンクリート造の6連アーチ橋で、橋長306.9メートル、幅員22.0メートルです。',
          source: 'https://www.hrr.mlit.go.jp/niikoku/bandaibridge/about.html',
          mapQuery: '萬代橋',
        },
        {
          id: 'bandai-1929',
          x: 0.24,
          y: 0.38,
          title: '昭和4年から現在まで',
          body: '三代目にあたる現在の橋は昭和4年（1929年）8月から使われています。重要文化財の指定は平成16年（2004年）7月6日です。',
          source: 'https://www.hrr.mlit.go.jp/niikoku/bandaibridge/about.html',
          mapQuery: '新潟市 萬代橋',
        },
        {
          id: 'bandai-jishin',
          x: 0.76,
          y: 0.64,
          title: '新潟地震に耐えた',
          body: '昭和39年（1964年）に起きた新潟地震に耐え、1日約3万台の自動車交通量を支える大動脈となっています。',
          source: 'https://www.hrr.mlit.go.jp/niikoku/bandaibridge/about.html',
          mapQuery: '萬代橋 信濃川',
        },
      ],
    },
  ],
};
